import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PieChart, type PieChartProps } from 'react-minimal-pie-chart';

import { Drawer, Flex, Tooltip, type TooltipProps, Typography } from 'antd';
import cn from 'classnames';

import { useMobileMediaQuery } from '@/shared/lib/hooks';

import cls from './trading-platform-statistic-pie-chart.module.css';

interface TradingPlatformStatisticPieChartWithTooltipProps {
    pieChartData: PieChartProps['data'];
    isPercent: boolean;
    winLoss?: [number, number] | undefined;
}

const TradingPlatformStatisticPieChartWithTooltip = memo(
    (props: TradingPlatformStatisticPieChartWithTooltipProps) => {
        const { pieChartData, isPercent, winLoss } = props;
        const { t } = useTranslation('trading-journal');

        const [hoveredSegment, setHoveredSegment] =
            useState<Nullable<number>>(null);

        const getTooltipParams = useMemo<
            Pick<
                TooltipProps,
                'color' | 'title' | 'open' | 'placement' | 'arrow'
            >
        >(() => {
            if (hoveredSegment !== null && pieChartData?.length) {
                const hoveredSegmentData = pieChartData[hoveredSegment];

                const value = winLoss?.length
                    ? winLoss[hoveredSegment]
                    : hoveredSegmentData?.value;

                return {
                    color: hoveredSegmentData?.color || '',
                    title: `${
                        hoveredSegment === 0
                            ? t('tradingJournalStatisticCardPieChartWinValue')
                            : t('tradingJournalStatisticCardPieChartLossValue')
                    }: ${value}${isPercent ? '%' : ''}`,
                    open: true,
                    placement: 'bottom',
                    arrow: false,
                };
            }

            return {};
        }, [hoveredSegment, pieChartData, winLoss, t, isPercent]);

        const onMouseChartOver = useCallback((_: unknown, index: number) => {
            setHoveredSegment(index);
        }, []);

        const onMouseChartOut = useCallback(() => {
            setHoveredSegment(null);
        }, []);

        return (
            <div className={cls.root}>
                {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                <Tooltip {...getTooltipParams} />
                <PieChart
                    data={pieChartData}
                    lineWidth={20}
                    onMouseOut={onMouseChartOut}
                    onMouseOver={onMouseChartOver}
                    startAngle={270}
                />
            </div>
        );
    },
);

TradingPlatformStatisticPieChartWithTooltip.displayName =
    'TradingPlatformStatisticPieChartWithTooltip';

interface TradingPlatformStatisticPieChartAsDrawerProps {
    titleKey: string;
    winPercent: number;
    pieChartData: PieChartProps['data'];
    winLoss?: [number, number] | undefined;
}

const TradingPlatformStatisticPieChartAsDrawer = memo(
    (props: TradingPlatformStatisticPieChartAsDrawerProps) => {
        const { titleKey, pieChartData, winPercent, winLoss } = props;

        const { t } = useTranslation('trading-journal');

        const [isOpened, setIsOpened] = useState<boolean>(false);

        const onOpen = useCallback(() => {
            setIsOpened(true);
        }, []);

        const onClose = useCallback(() => {
            setIsOpened(false);
        }, []);

        const roundedWinPercent = useMemo<number>(
            () => +winPercent.toFixed(2),
            [winPercent],
        );

        const roundedWin = useMemo<number>(
            () => +(winLoss?.length ? winLoss[0] : winPercent).toFixed(2),
            [winPercent, winLoss],
        );

        const roundedLoss = useMemo<number>(
            () => +(winLoss?.length ? winLoss[1] : 100 - winPercent).toFixed(2),
            [winPercent, winLoss],
        );

        return (
            <>
                <div
                    className={cls.root}
                    onClick={onOpen}
                >
                    <PieChart
                        data={pieChartData}
                        lineWidth={20}
                        startAngle={270}
                    />
                </div>
                <Drawer
                    closeIcon={null}
                    onClose={onClose}
                    open={isOpened}
                    placement="bottom"
                    rootClassName={cls.drawerContent}
                    title={t(titleKey)}
                >
                    <Flex
                        align="center"
                        className="full-height"
                        justify="space-around"
                        vertical
                    >
                        <div className={cls.chartContainer}>
                            <PieChart
                                data={pieChartData}
                                lineWidth={20}
                                startAngle={270}
                            />
                            <Typography.Title
                                className={cls.winPercentage}
                                level={4}
                            >
                                {roundedWinPercent}%
                            </Typography.Title>
                        </div>
                        <Flex
                            gap={16}
                            justify="center"
                        >
                            <Typography.Text className={cn(cls.stat, cls.win)}>
                                {t(
                                    'tradingJournalStatisticCardPieChartWinDrawerValue',
                                    { percent: roundedWin },
                                )}
                            </Typography.Text>
                            <Typography.Text className={cn(cls.stat, cls.loss)}>
                                {t(
                                    'tradingJournalStatisticCardPieChartLossDrawerValue',
                                    {
                                        percent: roundedLoss,
                                    },
                                )}
                            </Typography.Text>
                        </Flex>
                    </Flex>
                </Drawer>
            </>
        );
    },
);

TradingPlatformStatisticPieChartAsDrawer.displayName =
    'TradingPlatformStatisticPieChartAsDrawer';

interface TradingPlatformStatisticPieChartProps {
    titleKey: string;
    winPercent: number;
    winLoss?: [number, number] | undefined;
}

const winColor = '#26a69a';
const lossColor = '#FF4D4F';

export const TradingPlatformStatisticPieChart = memo(
    (props: TradingPlatformStatisticPieChartProps) => {
        const { titleKey, winPercent, winLoss } = props;

        const isMobile = useMobileMediaQuery();

        const pieChartData = useMemo<PieChartProps['data']>(() => {
            // const hasExactValues = winLoss?.length;
            // if (hasExactValues) {
            //     return [
            //         {
            //             value: +winLoss[0].toFixed(2),
            //             color: winColor,
            //         },
            //         {
            //             value: +winLoss[1].toFixed(2),
            //             color: lossColor,
            //         },
            //     ];
            // }

            const roundedWinPercent = +winPercent.toFixed(2);

            return [
                {
                    value: roundedWinPercent,
                    color: winColor,
                },
                {
                    value: +(100 - roundedWinPercent).toFixed(2),
                    color: lossColor,
                },
            ];
        }, [winPercent]);

        return !isMobile ? (
            <TradingPlatformStatisticPieChartWithTooltip
                isPercent={!winLoss?.length}
                pieChartData={pieChartData}
                winLoss={winLoss}
            />
        ) : (
            <TradingPlatformStatisticPieChartAsDrawer
                pieChartData={pieChartData}
                titleKey={titleKey}
                winLoss={winLoss}
                winPercent={winPercent}
            />
        );
    },
);

TradingPlatformStatisticPieChart.displayName =
    'TradingPlatformStatisticPieChart';
